import React from 'react';
import TaskIconRenderer from 'components/Client/Utility/TaskIconRenderer';

/**
 * A27・A09-3（2026-11-03 和田様の指摘）。タスクを 3行×5列の15枠の箱で並べる。
 *
 * - 埋まった枠はタスクの名前とアイコン。右上の「×」で onRemove（削除・外すの確認は呼び出し側）。
 * - 空の枠は「＋ 追加」の点線の箱。押すと onAdd（追加のダイアログは呼び出し側）。
 * - 15個を超えて持っている（以前に作った）ときは、全部を並べ、空の枠は出さない。
 * タスク管理（ManageTaskDialog）と、クライアントの登録・編集（TaskAssignment）で同じ並べ方にする。
 * 以前のタスク管理は3列で「削除」のモードに切り替えて消し、クライアントの登録は開始→終了の矢印の上に並べていた。
 */
export const TASK_SLOTS = 15;

export interface SlotTask {
  id: number | string;
  name: string;
  icon: string;
}

interface TaskSlotGridProps<T extends SlotTask> {
  tasks: T[];
  onAdd?: () => void;
  /** 空の枠の文字（例「タスク作成」「タスク追加」）。 */
  addLabel?: string;
  onRemove?: (task: T) => void;
  /** 「×」の読み上げの名前（例「{{name}} を削除」）。 */
  removeLabel: (task: T) => string;
  disabled?: boolean;
  /** 枠の数（既定は15）。 */
  slots?: number;
  /** 枠ごとに足す属性（並べ替えのドラッグなど）。 */
  slotProps?: (task: T, index: number) => React.HTMLAttributes<HTMLDivElement> & { draggable?: boolean };
}

function TaskSlotGrid<T extends SlotTask>({
  tasks,
  onAdd,
  addLabel = '',
  onRemove,
  removeLabel,
  disabled = false,
  slots = TASK_SLOTS,
  slotProps,
}: TaskSlotGridProps<T>) {
  const empty = onAdd ? Math.max(0, slots - tasks.length) : 0;
  return (
    <ul className="grid grid-cols-5 gap-3" data-task-slot-grid>
      {tasks.map((task, index) => (
        <li key={task.id} className="list-none">
          <div
            data-task-card
            className="relative flex flex-row items-center justify-between gap-2 h-16 px-3 border rounded-md shadow-sm bg-white"
            {...(slotProps ? slotProps(task, index) : {})}
          >
            {/* 名前（10文字まで）は「荷下ろし（バラ）」のように5列の枠に1行で入らないことがあるので、
                切らずに2行まで折り返す（11/4、和田様の UAT A27 の撮影で気づいた）。 */}
            <span className="text-neutral/300 text-body-2 leading-tight break-all line-clamp-2" title={task.name}>
              {task.name}
            </span>
            <span className="flex-shrink-0">
              <TaskIconRenderer iconName={task.icon} />
            </span>
            {onRemove ? (
              <button
                type="button"
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-neutral/50 text-neutral/200 flex items-center justify-center hover:text-error hover:border-error disabled:opacity-50"
                aria-label={removeLabel(task)}
                disabled={disabled}
                onClick={() => onRemove(task)}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M5.83301 5.8335L18.1663 18.1668M18.1663 5.8335L5.83301 18.1668"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        </li>
      ))}
      {Array.from({ length: empty }, (_, index) => (
        <li key={`empty-${index}`} className="list-none">
          <button
            type="button"
            data-task-empty-slot
            className="w-full h-16 rounded-md border-2 border-dashed border-primary/100 text-primary/300 flex flex-row items-center justify-center gap-1 text-caption hover:bg-primary/50 disabled:opacity-50"
            disabled={disabled}
            onClick={onAdd}
          >
            <span aria-hidden="true" className="text-subtitle-1">＋</span>
            {addLabel}
          </button>
        </li>
      ))}
    </ul>
  );
}

export default TaskSlotGrid;
